import { useRef, useState } from 'react';
import Icon from '../components/icon';
import Logo from '../components/logo';
import TextField from '../components/text-field';
import visualUrl from '../assets/login-visual.png';
import { signIn } from '../auth/client-session';
import { recoverPassword } from '../api/panel-api';
import { readStandId, rememberStand, stands } from '../config/stands';
import '../components/text-field.scss';
import './login-page.scss';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const credentialsError = 'Неверная почта или пароль. Проверьте раскладку и регистр.';
const recoverResultText = 'Если пользователь с такой почтой существует, мы отправили ссылку для восстановления пароля';

function hidesAccountExistence(error) {
  const code = String(error.code || '');
  const message = String(error.message || '');
  return error.status === 404
    || code === 'not_found'
    || code === 'user_not_found'
    || /пользователь не найден|почта не зарегистрир|не зарегистрирован/i.test(message);
}

function LoginPage({ onSignIn, initialMode = 'login' }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [authError, setAuthError] = useState('');
  const tabRefs = useRef([]);
  const [pending, setPending] = useState(false);
  const [standId, setStandId] = useState(readStandId);
  const standLabel = stands.find((item) => item.id === standId)?.label;
  const canSubmit = email.length > 0 && password.length > 0 && !pending;
  const fieldsInvalid = Boolean(authError);

  function handleEmailChange(nextEmail) {
    setEmail(nextEmail);
    setAuthError('');
    if (!nextEmail || emailPattern.test(nextEmail)) {
      setEmailError('');
    }
  }

  function handlePasswordChange(nextPassword) {
    setPassword(nextPassword);
    setAuthError('');
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    if (!emailPattern.test(email)) {
      setEmailError('Введите корректную электронную почту');
      setAuthError('');
      return;
    }
    setEmailError('');
    setPending(true);
    try {
      const session = await signIn(email, password);
      onSignIn(session);
    } catch (error) {
      const failedLogin = error.status === 401
        || error.status === 403
        || error.code === 'invalid_credentials'
        || error.code === 'unauthorized';
      setAuthError(failedLogin ? credentialsError : error.message);
    } finally {
      setPending(false);
    }
  }

  function showLogin() {
    setAuthError('');
    setEmailError('');
    setMode('login');
  }

  function showRecover() {
    setAuthError('');
    setEmailError('');
    setMode('recover');
  }

  async function handleRecover(event) {
    event.preventDefault();
    if (!email || pending) return;
    if (!emailPattern.test(email)) {
      setEmailError('Введите корректную электронную почту');
      return;
    }
    setPending(true);
    setAuthError('');
    setEmailError('');
    try {
      await recoverPassword(email);
      setMode('sent');
    } catch (error) {
      if (hidesAccountExistence(error)) {
        setMode('sent');
      } else {
        setAuthError(error.message);
      }
    } finally {
      setPending(false);
    }
  }

  function moveTabFocus(currentIndex, direction) {
    const nextIndex = (currentIndex + direction + stands.length) % stands.length;
    tabRefs.current[nextIndex]?.focus();
  }

  function handleTabKeyDown(event, index) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveTabFocus(index, 1);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveTabFocus(index, -1);
    }
    if (event.key === 'Home') {
      event.preventDefault();
      tabRefs.current[0]?.focus();
    }
    if (event.key === 'End') {
      event.preventDefault();
      tabRefs.current[stands.length - 1]?.focus();
    }
  }

  function openStand(stand) {
    if (stand.id === standId) {
      return;
    }
    if (stand.href) {
      window.location.assign(stand.href);
      return;
    }
    rememberStand(stand.id);
    setStandId(stand.id);
  }

  return (
    <main className="login">
      <div className="login__visual">
        <Logo />
        <img
          className="login__art"
          src={visualUrl}
          alt=""
          width="629"
          height="656"
        />
      </div>
      <section className="login__panel" aria-label="Вход">
        {mode === 'recover' ? (
          <form className="login__form" noValidate onSubmit={handleRecover}>
            <header className="login__head">
              <h1 className="login__title">Восстановление доступа</h1>
              <p className="login__lead">Пришлём ссылку для восстановления доступа на рабочую почту</p>
            </header>
            {authError ? (
              <p className="login__alert" role="alert">
                <svg className="login__alert-icon" viewBox="0 0 20 20" aria-hidden="true">
                  <circle cx="10" cy="10" r="7.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M10 6.25v4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="10" cy="13.75" r="0.75" fill="currentColor" />
                </svg>
                <span>{authError}</span>
              </p>
            ) : null}
            <TextField label="Электронная почта" type="email" value={email} error={emailError} required trailing="clear" onChange={handleEmailChange} />
            <div className="login__actions">
              <button className="login__submit" type="submit" disabled={!email || pending}>Отправить ссылку</button>
              <button type="button" className="login__secondary" onClick={showLogin}>К форме входа</button>
            </div>
          </form>
        ) : null}
        {mode === 'sent' ? (
          <div className="login__form">
            <header className="login__head">
              <h1 className="login__title">Восстановление доступа</h1>
            </header>
            <p className="login__alert login__alert--info" role="status">
              <svg className="login__alert-icon" viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="10" cy="10" r="7.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10 9.25v4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="10" cy="6.75" r="0.75" fill="currentColor" />
              </svg>
              <span>{recoverResultText}</span>
            </p>
            <button type="button" className="login__secondary" onClick={showLogin}>К форме входа</button>
          </div>
        ) : null}
        {mode === 'session' ? (
          <div className="login__form">
            <div className="login__notice-icon"><Icon name="clock-large" size={26} /></div>
            <header className="login__head">
              <h1 className="login__title">Сессия истекла</h1>
              <p className="login__lead">Войдите ещё раз, чтобы продолжить работу.</p>
            </header>
            <button type="button" className="login__submit" onClick={showLogin}>Войти</button>
          </div>
        ) : null}
        {mode === 'login' ? (
        <form className="login__form" noValidate onSubmit={handleSubmit}>
          <header className="login__head">
            <h1 className="login__title">Вход в панель RHM</h1>
            <p className="login__lead">Войдите по рабочей почте и паролю</p>
          </header>
          <div className="stands">
            <p className="stands__label" id="stand-label">Рабочий стенд</p>
            <div className="stands__tabs" role="tablist" aria-labelledby="stand-label">
              {stands.map((stand, index) => {
                const selected = stand.id === standId;
                return (
                  <button
                    key={stand.id}
                    ref={(node) => {
                      tabRefs.current[index] = node;
                    }}
                    type="button"
                    className={selected ? 'stands__tab is-active' : 'stands__tab'}
                    role="tab"
                    aria-selected={selected}
                    tabIndex={selected ? 0 : -1}
                    onClick={() => openStand(stand)}
                    onKeyDown={(event) => handleTabKeyDown(event, index)}
                  >
                    {stand.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="login__fields">
            {authError ? (
              <p className="login__alert" role="alert">
                <svg className="login__alert-icon" viewBox="0 0 20 20" aria-hidden="true">
                  <circle cx="10" cy="10" r="7.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M10 6.25v4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="10" cy="13.75" r="0.75" fill="currentColor" />
                </svg>
                <span>{authError}</span>
              </p>
            ) : null}
            <TextField
              label="Электронная почта"
              type="email"
              value={email}
              autoComplete="email"
              error={emailError}
              trailing="clear"
              invalid={fieldsInvalid}
              onChange={handleEmailChange}
            />
            <TextField
              label="Пароль"
              type="password"
              value={password}
              autoComplete="current-password"
              trailing="password"
              invalid={fieldsInvalid}
              onChange={handlePasswordChange}
            />
            <div className="login__recover">
              <button type="button" className="login__link" onClick={showRecover}>
                Забыли пароль?
              </button>
            </div>
          </div>
          <button className="login__submit" type="submit" disabled={!canSubmit}>
            Войти
          </button>
        </form>
        ) : null}
        {mode !== 'login' ? (
          <p className="login__stand">Рабочий стенд: {standLabel}</p>
        ) : null}
      </section>
    </main>
  );
}

export default LoginPage;
